import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { IAccount } from '../../interfaces/accounting.interface';
import AccountSelect from './AccountSelect';
import { EditableLine, formatMoney, isBalanced, sumLines, today } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

const emptyLine = (): EditableLine => ({ accountId: '', debit: '', credit: '', memo: '' });

const amountOrNull = (v: string): number | null => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const JournalEntryForm: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const navigate = useNavigate();
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [date, setDate] = useState(today());
    const [description, setDescription] = useState('');
    const [lines, setLines] = useState<EditableLine[]>([emptyLine(), emptyLine()]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!institutionId) return;
        AccountingService.listAccounts(institutionId).then(setAccounts)
            .catch(error => showApiError(error, t('errors.loadAccounts')));
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = (i: number, patch: Partial<EditableLine>) =>
        setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

    const totals = sumLines(lines);
    const difference = Math.round((totals.debit - totals.credit) * 100) / 100;
    const canSave = isBalanced(lines) && !!description.trim() && !!date && !saving;

    const save = async () => {
        setSaving(true);
        try {
            const entry = await AccountingService.createEntry(institutionId, {
                entryDate: date,
                description: description.trim(),
                lines: lines.map(l => ({
                    accountId: l.accountId, debit: amountOrNull(l.debit), credit: amountOrNull(l.credit),
                    memo: l.memo?.trim() || null,
                })),
            });
            showSuccess(t('journalEntryForm.posted'));
            navigate(`/accounting/entries/${entry.id}`);
        } catch (error) {
            showApiError(error, t('journalEntryForm.postFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>{t('journalEntryForm.title')}</Typography>
            <Paper sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField id="je-date" label={t('fields.date')} type="date" size="small" value={date}
                               onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField id="je-description" label={t('fields.description')} size="small" fullWidth value={description}
                               onChange={e => setDescription(e.target.value)} inputProps={{ maxLength: 255 }} />
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 220 }}>{t('fields.account')}</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>{t('fields.debit')}</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>{t('fields.credit')}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>{t('fields.memo')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((l, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <AccountSelect id={`je-line-${i}-account`} label={t('fields.account')} value={l.accountId}
                                                       accounts={accounts} onChange={v => update(i, { accountId: v })} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField id={`je-line-${i}-debit`} label={t('fields.debit')} type="number" size="small" value={l.debit}
                                                   onChange={e => update(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                                                   inputProps={{ min: 0, step: '0.01' }} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField id={`je-line-${i}-credit`} label={t('fields.credit')} type="number" size="small" value={l.credit}
                                                   onChange={e => update(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
                                                   inputProps={{ min: 0, step: '0.01' }} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField id={`je-line-${i}-memo`} label={t('fields.memo')} size="small" value={l.memo ?? ''}
                                                   onChange={e => update(i, { memo: e.target.value })} inputProps={{ maxLength: 255 }} />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton aria-label={t('journalEntryForm.removeLine')} disabled={lines.length <= 2}
                                                    onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell><strong>{t('journalEntryForm.totals')}</strong></TableCell>
                                <TableCell><strong>{formatMoney(totals.debit)}</strong></TableCell>
                                <TableCell><strong>{formatMoney(totals.credit)}</strong></TableCell>
                                <TableCell colSpan={2} />
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
                {difference !== 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>{t('journalEntryForm.difference', { amount: formatMoney(Math.abs(difference)) })}</Alert>
                )}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button onClick={() => setLines(prev => [...prev, emptyLine()])}>{t('journalEntryForm.addLine')}</Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => navigate('/accounting/entries')}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" disabled={!canSave} onClick={save}>{t('journalEntryForm.saveEntry')}</Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default JournalEntryForm;
